/**
 * Consolidated Statement Generator
 *
 * Generates consolidated financial statements for enterprise customers showing
 * all invoices and payments across multiple branches.
 *
 * Phase 3B - Consolidated Statement Generation Logic
 * User Story: US-005 (Invoice Generation Enhancement)
 */

import { supabase } from '../supabase-client'

/**
 * Generate consolidated statement data for a customer
 *
 * @param {string} customerId - UUID of the customer
 * @param {string} startDate - Start date for the statement period (YYYY-MM-DD)
 * @param {string} endDate - End date for the statement period (YYYY-MM-DD)
 * @returns {Promise<Object>} Consolidated statement data structure
 * @throws {Error} If customer not found or query fails
 */
export const generateConsolidatedStatement = async (customerId, startDate, endDate) => {
  if (!customerId) {
    throw new Error('Customer ID is required')
  }

  if (!startDate || !endDate) {
    throw new Error('Start date and end date are required')
  }

  // Validate date format (basic check)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
    throw new Error('Dates must be in YYYY-MM-DD format')
  }

  // Validate date range
  if (new Date(startDate) > new Date(endDate)) {
    throw new Error('Start date must be before or equal to end date')
  }

  try {
    // Fetch all data from view with date filtering
    const { data, error } = await supabase
      .from('customer_consolidated_statements')
      .select('*')
      .eq('customer_id', customerId)
      .gte('invoice_date', startDate)
      .lte('invoice_date', endDate)

    if (error) {
      console.error('Error fetching consolidated statement data:', error)
      throw new Error(`Failed to fetch consolidated statement: ${error.message}`)
    }

    if (!data || data.length === 0) {
      throw new Error('No data found for this customer in the specified date range')
    }

    // Extract customer information (same across all rows)
    const customerInfo = {
      id: data[0].customer_id,
      name: data[0].customer_name,
      email: data[0].email,
      type: data[0].customer_type
    }

    // Group data by branch
    const branchGroups = groupByBranch(data)

    // Calculate consolidated totals
    const totals = calculateConsolidatedTotals(branchGroups)

    return {
      customerInfo,
      branchGroups,
      totals,
      startDate,
      endDate,
      generatedAt: new Date().toISOString()
    }
  } catch (error) {
    console.error('Error generating consolidated statement:', error)
    throw error
  }
}

/**
 * Group statement data by branch
 *
 * @param {Array<Object>} data - Raw data from customer_consolidated_statements view
 * @returns {Object} Data grouped by branch_id
 */
function groupByBranch(data) {
  const groups = {}

  data.forEach(row => {
    const branchId = row.branch_id

    // Initialize branch group if not exists
    if (!groups[branchId]) {
      groups[branchId] = {
        branchId,
        branchName: row.branch_name,
        costCenterCode: row.cost_center_code,
        invoices: [],
        payments: []
      }
    }

    // Add invoice to branch group (avoid duplicates)
    if (row.invoice_id && !groups[branchId].invoices.some(inv => inv.id === row.invoice_id)) {
      groups[branchId].invoices.push({
        id: row.invoice_id,
        invoiceNo: row.invoice_no,
        date: row.invoice_date,
        total: parseFloat(row.invoice_total),
        paid: row.invoice_paid
      })
    }

    // Add payment to branch group (avoid duplicates)
    if (row.payment_id && !groups[branchId].payments.some(pay => pay.id === row.payment_id)) {
      groups[branchId].payments.push({
        id: row.payment_id,
        date: row.payment_date,
        amount: parseFloat(row.payment_amount),
        method: row.payment_method
      })
    }
  })

  // Sort invoices and payments by date within each branch
  Object.values(groups).forEach(branch => {
    branch.invoices.sort((a, b) => new Date(b.date) - new Date(a.date)) // Newest first
    branch.payments.sort((a, b) => new Date(b.date) - new Date(a.date)) // Newest first
  })

  return groups
}

/**
 * Calculate consolidated totals across all branches
 *
 * @param {Object} branchGroups - Data grouped by branch
 * @returns {Object} Consolidated financial totals
 */
function calculateConsolidatedTotals(branchGroups) {
  let totalInvoiced = 0
  let totalPaid = 0
  let totalOutstandingInvoices = 0

  Object.values(branchGroups).forEach(branch => {
    // Sum all invoices for this branch
    const branchInvoiced = branch.invoices.reduce((sum, inv) => sum + inv.total, 0)

    // Sum unpaid invoices for this branch
    const branchOutstanding = branch.invoices
      .filter(inv => !inv.paid)
      .reduce((sum, inv) => sum + inv.total, 0)

    // Sum all payments for this branch
    const branchPaidPayments = branch.payments.reduce((sum, pay) => sum + pay.amount, 0)

    totalInvoiced += branchInvoiced
    totalOutstandingInvoices += branchOutstanding
    totalPaid += branchPaidPayments
  })

  // Calculate total outstanding (unpaid invoices)
  const totalOutstanding = totalOutstandingInvoices

  // Calculate current balance (payments - outstanding invoices)
  // Positive = customer credit, Negative = customer owes
  const currentBalance = totalPaid - totalOutstanding

  return {
    totalInvoiced: parseFloat(totalInvoiced.toFixed(2)),
    totalPaid: parseFloat(totalPaid.toFixed(2)),
    totalOutstanding: parseFloat(totalOutstanding.toFixed(2)),
    currentBalance: parseFloat(currentBalance.toFixed(2))
  }
}

/**
 * Get branch count for a customer (utility function)
 *
 * @param {string} customerId - UUID of the customer
 * @returns {Promise<number>} Number of branches for this customer
 */
export const getCustomerBranchCount = async (customerId) => {
  try {
    const { count, error } = await supabase
      .from('branches')
      .select('*', { count: 'exact', head: true })
      .eq('customer_id', customerId)

    if (error) {
      console.error('Error fetching branch count:', error)
      return 0
    }

    return count || 0
  } catch (error) {
    console.error('Error in getCustomerBranchCount:', error)
    return 0
  }
}

/**
 * Check if customer is eligible for consolidated statements
 *
 * @param {Object} customer - Customer object with branches
 * @returns {boolean} True if customer is eligible for consolidated statements
 */
export const isEligibleForConsolidatedStatement = (customer) => {
  // Must have multiple branches
  const branchCount = customer?.branches?.length || 0
  if (branchCount <= 1) {
    return false
  }

  // Check if ANY branch is enterprise type
  const hasEnterpriseBranch = customer?.branches?.some(
    branch => branch.customer_type?.name === 'enterprise'
  )
  if (!customer || !hasEnterpriseBranch) {
    return false
  }

  return true
}
